import React, {PureComponent} from 'react';
import { UserContext } from "../../core/UserContext";
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import Container from '@mui/material/Container';
import {Regexer} from "../../core/auth/Regexer";
import CircularProgress from "@mui/material/CircularProgress";
import {PageDataContext} from "../../core/PageDataContext";
import {Divider, IconButton, InputAdornment} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {withRouter} from "../../hooks/withRouter";
import {Link as RouterLink} from "react-router-dom";
import {UrlBuilder} from "../../core/url/UrlBuilder";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import moment from "moment";
import PersonIcon from "@mui/icons-material/Person";
import WIPIcon from "../../res/img/icon.png";

const useStyles = theme => ({
    form: {
        width: '100%',
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
});

class Signup extends PureComponent {
    static displayName = Signup.name;

    constructor(props) {
        super(props);
        this.state ={
            isLoading: false,
            isEmailAlreadyTaken: false,
            showPassword: false,
            isSigningUpAsGuest: false,
        };

        this.updateInputValue = this.updateInputValue.bind(this);
        this.submit = this.submit.bind(this);
    }

    componentDidMount() {
        this.context.setPageData({
            title: "Sign Up to My Work in Progress"
        });
    }

    updateInputValue(event) {
        this.setState({
            [event.target.name]: event.target.value,
            isEmailAlreadyTaken: false,
        });
    }

    redirectToGoogleAuth = () => {
        window.location = '/api/users/google-signin';
    }

    redirectToAppleAuth = () => {
        window.location = '/api/users/apple-signin';
    }

    async submit(event, setUser) {
        event.preventDefault();

        this.setState({
            isLoading: true
        });

        let signupUser = {
            name: this.state.name,
            email: this.state.email,
            password: this.state.password,
            dateCreated: moment().format('L, h:mm:ss A'),
        };

        let response = await fetch('/api/users/signup', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(signupUser)
        });

        let jsonResponse = await response.json();

        if (response.status === 200) {
            setUser(jsonResponse['data']);
            this.props.navigate('/');
        } else if (jsonResponse.message === "email_already_taken") {
            this.setState({
                isEmailAlreadyTaken: true,
                isLoading: false,
            })
        } else {
            this.setState({
                isLoading: false,
            })
        }
    }

    showPassword = () => {
        this.setState({
            showPassword: !this.state.showPassword,
        });
    }

    signUpAsGuest = async (event, setUser) => {
        event.preventDefault();

        this.setState({
            isSigningUpAsGuest: true
        });

        let guestUser = {
            dateCreated: moment().format('L, h:mm:ss A'),
        };

        let response = await fetch(UrlBuilder.user.SignUpAsGuestApi(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(guestUser)
        });

        let jsonResponse = await response.json();

        if (response.status === 200) {
            setUser(jsonResponse['data']);
            this.props.navigate('/');
        }

        this.setState({
            isSigningUpAsGuest: false,
        })
    }

    render() {
        const { classes } = this.props;

        let isEmailInvalid = false;
        let emailFieldHelperText = null;
        if (this.state.email && !Regexer.testEmail(this.state.email)) {
            isEmailInvalid = true;
            emailFieldHelperText = 'Please enter a valid email'
        } else if (this.state.isEmailAlreadyTaken) {
            isEmailInvalid = true;
            emailFieldHelperText = 'Email is already taken, please try another one';
        }

        let isPasswordInvalid = false;
        let passwordFieldHelperText = null;
        if (this.state.password && !Regexer.testPassword(this.state.password)) {
            isPasswordInvalid = true;
            passwordFieldHelperText = 'Password must have a minimum length of 6 and contain a number.';
        }

        let isSubmitDisabled = !this.state.name || !this.state.email || isEmailInvalid || !this.state.password || isPasswordInvalid;

        return (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box sx={{
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Box sx={{
                        width: '100%',
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <img src={WIPIcon} width={48} alt="WIP" style={{ borderRadius: 12, marginBottom: 12 }} />
                            <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                                Create your account
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                Start tracking in seconds
                            </Typography>
                        </Box>

                        <Grid container direction='column' display="flex" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <Grid item>
                                <Button sx={{ width: 280, height: 44 }} variant='contained'
                                        onClick={this.redirectToGoogleAuth} size='medium'
                                        startIcon={<GoogleIcon />}>
                                    Continue with Google
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button sx={{ width: 280, height: 44 }} variant='contained'
                                        onClick={this.redirectToAppleAuth} size='medium'
                                        startIcon={<AppleIcon />}>
                                    Continue with Apple
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>or with email</Typography>
                        </Divider>

                        <form className={classes.form} noValidate>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        size="small"
                                        id="name"
                                        label="Name"
                                        name="name"
                                        onChange={this.updateInputValue}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        size="small"
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        autoComplete="email"
                                        onChange={this.updateInputValue}
                                        error={isEmailInvalid}
                                        helperText={emailFieldHelperText}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment position="end">
                                                    <IconButton onClick={this.showPassword} edge="end" size="small">
                                                        {this.state.showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                        }}
                                        variant="outlined"
                                        required
                                        fullWidth
                                        size="small"
                                        name="password"
                                        label="Password"
                                        type={this.state.showPassword ? "text" : "password"}
                                        id="password"
                                        autoComplete="current-password"
                                        onChange={this.updateInputValue}
                                        error={isPasswordInvalid}
                                        helperText={passwordFieldHelperText}
                                    />
                                </Grid>
                            </Grid>
                            <UserContext.Consumer>
                                {({ user, setUser }) => (
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        sx={{ mt: 2, mb: 1, height: 44 }}
                                        onClick={e => this.submit(e, setUser)}
                                        disabled={isSubmitDisabled}
                                    >
                                        {
                                            this.state.isLoading ?
                                                <div>
                                                    <CircularProgress size="1rem" color="inherit" /> Sign up
                                                </div> :
                                                <div>
                                                    Sign up
                                                </div>
                                        }
                                    </Button>
                                )}
                            </UserContext.Consumer>
                        </form>
                    </Box>

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Already have an account?{' '}
                            <Link component={RouterLink} to={UrlBuilder.user.SignInPage()} sx={{ color: 'secondary.main', fontWeight: 600 }}>
                                Sign In
                            </Link>
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                        <UserContext.Consumer>
                            {({user, setUser}) => (
                                <Button
                                    type="submit"
                                    startIcon={this.state.isSigningUpAsGuest ? <CircularProgress size="1rem" color="inherit" /> : <PersonIcon/>}
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    sx={{ fontSize: 13, textTransform: 'none' }}
                                    onClick={e => this.signUpAsGuest(e, setUser)}
                                    disabled={this.state.isSigningUpAsGuest}
                                >
                                    Try as Guest
                                </Button>
                            )}
                        </UserContext.Consumer>
                    </Box>

                    <Box mt={4}>
                        <Typography variant="body2" color="textSecondary" align="center">
                            {'Copyright © My Work in Progress '}
                            {new Date().getFullYear()}{'. '}
                            <Link component={RouterLink} to={UrlBuilder.user.privacyPage()} sx={{ color: 'text.secondary' }}>
                                Privacy Policy
                            </Link>
                        </Typography>
                    </Box>
                </Box>
            </Container>
        );
    }
}

Signup.contextType = PageDataContext;

export default withStyles(useStyles)(withRouter(Signup));
